import React, { useEffect, useRef, useState } from "react";
import { Users, CheckCircle, School, Award } from "lucide-react";
import { stats } from "../mock";

const iconMap = {
  users: Users,
  "check-circle": CheckCircle,
  school: School,
  award: Award,
};

const useCounter = (target, start) => {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!start) return;

    const duration = 1800;
    const stepTime = 16;
    const steps = duration / stepTime;
    const inc = target / steps;

    let cur = 0;

    const id = setInterval(() => {
      cur += inc;

      if (cur >= target) {
        setVal(target);
        clearInterval(id);
      } else {
        setVal(Math.floor(cur));
      }
    }, stepTime);

    return () => clearInterval(id);
  }, [target, start]);

  return val;
};

const StatItem = ({ stat, start }) => {
  const Icon = iconMap[stat.icon] || Users;
  const value = useCounter(stat.value, start);

  return (
    <div className="group flex items-center gap-5">
      <div className="w-[72px] h-[72px] rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[#ffd28a] flex-shrink-0 transition-all duration-300 group-hover:bg-[#ff6b00] group-hover:border-[#ff6b00] group-hover:text-white">
        <Icon size={30} strokeWidth={2} />
      </div>

      <div>
        <div className="flex items-start">
          <span className="text-4xl md:text-5xl font-black text-white tabular-nums">
            {value.toLocaleString()}
          </span>

          <span className="text-2xl font-bold text-[#ff6b00] ml-0.5">
            +
          </span>
        </div>

        <p className="text-[#ff6b00] font-semibold mt-1 text-[15px] tracking-wide">
          {stat.label}
        </p>
      </div>
    </div>
  );
};

const Stats = () => {
  const [start, setStart] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setStart(true);
        }),
      { threshold: 0.3 }
    );

    if (ref.current) obs.observe(ref.current);

    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-16 bg-[#06183f] overflow-hidden"
    >
      {/* decorative glows */}
      <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#1f9d4c]/15 blur-3xl pointer-events-none" />

      <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-[#ff6b00]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        
        {/* Heading */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-[3px] bg-[#ff6b00]" />

            <span className="uppercase text-[#ff6b00] font-bold tracking-[0.2em] text-sm">
              Impact in Numbers
            </span>

            <span className="w-12 h-[3px] bg-[#ff6b00]" />
          </div>

          <h2
            className="text-4xl md:text-5xl font-black text-white leading-tight"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Strengthening Basic Education Across Akwa Ibom
          </h2>
        </div>

        {/* Small Intro Text */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <p className="text-white/90 text-[16px] md:text-[18px] leading-8 font-medium">
            The Akwa Ibom State Universal Basic Education Board, AK-SUBEB,
            has continued to make measurable progress in strengthening
            basic education across the State through strategic investments,
            targeted interventions and result-driven programmes.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s) => (
            <StatItem key={s.id} stat={s} start={start} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;